import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowRight } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import { useTenantSlug } from '../../context/TenantSlugContext';
import type { TenantHomePageContent } from '../../types/site-pages';
import { buildTenantPath } from '../../utils/tenantSlug';

interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  publishDate: string;
  author: string;
  category: string;
}

interface NewsPreviewProps {
  tenantContent?: TenantHomePageContent | null;
}

const NewsPreview = ({ tenantContent }: NewsPreviewProps) => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { selectedClient, isLoading: authLoading } = useAuth();
  const { tenantSlug } = useTenantSlug();
  const effectiveSlug = tenantSlug || selectedClient?.clientSlug || null;
  const title = tenantContent?.newsPreview?.title || 'Notícias e Artigos';
  const subtitle =
    tenantContent?.newsPreview?.subtitle ||
    'Fique por dentro das novidades, técnicas e avanços na área de fisioterapia e reabilitação.';
  const viewAllLabel = tenantContent?.newsPreview?.viewAllLabel || 'Ver todas as notícias';

  useEffect(() => {
    const fetchNews = async () => {
      if (authLoading) return;

      if (!effectiveSlug) {
        setNews([]);
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc("get_public_news_by_slug", { p_slug: effectiveSlug });


        if (error) {
          throw error;
        }

        const newsData = ((data || []) as any[])
          .map((item) => ({
            id: item.newspageId,
            title: item.newspageTitle,
            excerpt: item.newspageExcerpt,
            image: item.newspageImage,
            publishDate: item.newspagePublishDate,
            author: item.newspageAuthor,
            category: item.newspageCategory,
          }))
          .slice(0, 3) as NewsItem[];

        setNews(newsData);
      } catch (error) {
        console.error("Error fetching news:", error);
        setNews([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();
  }, [authLoading, effectiveSlug]);

  if (!authLoading && !effectiveSlug) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  if (isLoading) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-12"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-4">
              {title}
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl">
              {subtitle}
            </p>
          </div>
          <Link
            to={buildTenantPath("/noticias", effectiveSlug)}
            className="mt-4 md:mt-0 flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            {viewAllLabel} <ArrowRight size={18} className="ml-1" />
          </Link>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {news.map((item) => (
            <motion.article
              key={item.id}
              className="bg-white border border-neutral-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
              variants={itemVariants}
            >
              <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover object-center transition-transform hover:scale-105"
                />
              </div>

              <div className="p-6">
                <div className="flex items-center text-sm text-neutral-500 mb-3">
                  <span>{format(new Date(item.publishDate), "d 'de' MMMM 'de' yyyy", { locale: ptBR })}</span>
                  <span className="mx-2">•</span>
                  <span>{item.category}</span>
                </div>

                <h3 className="text-xl font-display font-semibold text-neutral-900 mb-3 line-clamp-2">
                  <Link to={buildTenantPath(`/noticias/${item.id}`, effectiveSlug)} className="hover:text-primary-600 transition-colors">
                    {item.title}
                  </Link>
                </h3>

                <p className="text-neutral-600 mb-4 line-clamp-3">
                  {item.excerpt}
                </p>

                <Link
                  to={buildTenantPath(`/noticias/${item.id}`, effectiveSlug)}
                  className="text-primary-600 hover:text-primary-700 font-medium flex items-center transition-colors"
                >
                  Ler mais <ArrowRight size={16} className="ml-1" />
                </Link>
              </div>
            </motion.article>
          ))}
        </motion.div>

        {news.length === 0 && (
          <div className="text-center py-8">
            <p className="text-neutral-600">Nenhuma notícia disponível no momento.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsPreview;
